/**
 * Team Data - DEPRECATED for Authentication
 *
 * ⚠️ IMPORTANT: This static array is DEPRECATED for authentication purposes.
 *
 * As of January 2026, team member authentication has been migrated to Firestore.
 * The LandingPage now loads users from the 'users' collection instead of this array.
 *
 * CURRENT STATUS:
 * - ❌ NOT USED: Login/Authentication (now uses Firestore 'users' collection)
 * - ❌ NOT USED: Team member management (use Settings Team page at /settings/team)
 * - ✅ STILL USED: PROVISIONING_PIN constant only
 *
 * WHAT TO USE INSTEAD:
 * - Team Members: Firestore 'users' collection (managed via /settings/team)
 * - Authentication: LandingPage.tsx loads from Firestore
 * - Team Data Hook: useTeam() reads from Firestore
 *
 * TO ADD NEW TEAM MEMBERS:
 * 1. Navigate to /settings/team in the app
 * 2. Click "Add User"
 * 3. Fill in the form with user details
 * 4. User will immediately appear in login
 *
 * DO NOT edit this file to add new team members.
 *
 * @see docs/TEAM_AUTHENTICATION.md for complete documentation
 * @deprecated Use Firestore 'users' collection instead
 */

export interface TeamMember {
  id: string;
  name: string;
  role: 'office' | 'sales' | 'ops' | 'tech' | 'provisioning';
  pin: string;
}

export const TEAM: TeamMember[] = [
  // Core Office Team - All route to customizable Grid Dashboard
  { id: 'casey', name: 'Casey', role: 'office', pin: '6781' },     // Routes to Grid Dashboard
  { id: 'nicole', name: 'Nicole', role: 'office', pin: '6781' },   // Routes to Grid Dashboard
  { id: 'reuben', name: 'Reuben', role: 'office', pin: '2571' },   // Routes to Grid Dashboard

  // Field Technicians
  // NOTE: Reuben and Guy have dual access (office + tech) using same PIN
  // In Firestore 'users' collection, they have dualAccess: true
  // This allows them to appear in both office and tech login lists
  // Routing is determined by which list they click from (office → /home, tech → /tech)
  { id: 'reuben-tech', name: 'Reuben', role: 'tech', pin: '2571' },  // DEPRECATED: Use dualAccess in Firestore instead
  { id: 'lewis', name: 'Lewis', role: 'tech', pin: '0000' },
  { id: 'seleni', name: 'Seleni', role: 'tech', pin: '0000' },
  { id: 'jamie', name: 'Jamie', role: 'tech', pin: '0000' },
  { id: 'jayden', name: 'Jayden', role: 'tech', pin: '0000' },
  { id: 'guy', name: 'Guy', role: 'tech', pin: '0000' },  // DEPRECATED: Now has dualAccess: true in Firestore

  // Equipment Provisioning
  { id: 'provisioning', name: 'Provisioning', role: 'provisioning', pin: '6781' }
];

/**
 * Techs who should be hidden from schedule views UNLESS they're scheduled
 * for jobs in the current window. Match is by first name (case-insensitive)
 * to stay stable across Firestore user id conventions.
 *
 * Rationale: dual-access / office-first users like Guy go into the field only
 * occasionally — leaving them always-visible adds tech columns to the scheduler
 * and rows to /tech/team that are empty >95% of the time. Hiding them when
 * unscheduled keeps the views focused on who's actually working that day/week.
 */
export const SCHEDULE_HIDDEN_UNLESS_SCHEDULED = ['guy', 'brady'] as const;

export function isHiddenUnlessScheduled(techName: string | undefined): boolean {
  if (!techName) return false;
  const first = techName.split(' ')[0]?.toLowerCase();
  return (SCHEDULE_HIDDEN_UNLESS_SCHEDULED as readonly string[]).includes(first);
}

/**
 * Fallback name list for dual-access detection — used when the Firestore
 * `dualAccess` flag isn't set on a user record yet. These people work both
 * in the office and in the field, so they should always see the "Field Day"
 * pill in /home regardless of whether they have jobs scheduled.
 *
 * Match is by first name (case-insensitive). Prefer setting `dualAccess: true`
 * on the user document in Firestore — this list is just a safety net.
 */
export const DUAL_ACCESS_FALLBACK_NAMES = ['reuben', 'guy', 'brady'] as const;

export function isDualAccessFallback(userName: string | undefined): boolean {
  if (!userName) return false;
  const first = userName.split(' ')[0]?.toLowerCase();
  return (DUAL_ACCESS_FALLBACK_NAMES as readonly string[]).includes(first);
}

// PIN configuration (from legacy system)
export const OFFICE_PIN = '6781';  // Office staff PIN
export const PROVISIONING_PIN = '6781';  // Provisioning PIN
